import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { takeUntil } from 'rxjs';

import { IVisualCardWithConfig, SdVisualCardsComponent } from '@seb/web-design-system/visual-cards';
import {
  BrandsEnum,
  ComponentWrapperWidthEnum,
  ImageRatioEnum,
  PositionEnum,
  PushSizeEnum
} from '@seb/web-design-system/core';

import { VisualCardComponentInterface, VisualCardModuleComponentInterface, visualCardWithConfigSkeleton } from '../../../models';
import { HeadingParagraphMapper, ThemesMapper, transformHeadlineLevelToTitleLevel } from '../../../mappers';
import { AbstractModuleComponent } from '../abstract-module.component';
import { WrapperComponent } from '../../../components/static/core';
import { ImageService } from '../../../services';

@Component({
  selector: 'wc-visual-cards',
  templateUrl: './visual-cards-module.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [WrapperComponent, SdVisualCardsComponent]
})
export class VisualCardsModuleComponent extends AbstractModuleComponent<VisualCardModuleComponentInterface> implements OnInit {
  cards: IVisualCardWithConfig[];
  size: PushSizeEnum;
  titleLevel: number;
  isDescription: boolean;
  invertTitle: boolean;
  absoluteWidth: ComponentWrapperWidthEnum;
  textConfig: PositionEnum;
  cardLayout: PositionEnum;
  imageRatio: ImageRatioEnum;

  constructor(
    private imageService: ImageService,
    private headingParagraphMapper: HeadingParagraphMapper,
    private cdRef: ChangeDetectorRef
  ) {
    super();
  }

  override onModuleInit(module: VisualCardModuleComponentInterface): void {
    if (this.useLazyLoad) {
      this.cards = new Array(module.components?.split(' ').length).fill(undefined).map(
        () => ({ ...visualCardWithConfigSkeleton })
      );
    }

    this.invertTitle = module.invertTitle === 'true';
    this.isDescription = module.isDescription === 'true';
    this.absoluteWidth = module.isFullWidth === 'true' ? ComponentWrapperWidthEnum.absoluteFullWidth : ComponentWrapperWidthEnum.default;
    this.size = module.size?.toLowerCase() as PushSizeEnum;
    this.textConfig = module.textConfig?.toLowerCase() as PositionEnum;
    this.cardLayout = module.cardLayout?.toLowerCase() as PositionEnum;
    this.imageRatio = module.imageRatio?.toLowerCase() as ImageRatioEnum;

    this.titleLevel = transformHeadlineLevelToTitleLevel(module.titleLevel);
    this.cdRef.markForCheck();
  }

  ngOnInit(): void {
    this.module$.pipe(takeUntil(this.destroy$)).subscribe((module: VisualCardModuleComponentInterface) => {
      this.cards = module.componentsObjects?.map((card) => {
        let brand = this.brandCorner;
        if (!brand) {
          const cmpBrand = card.brandCorner;
          const isValidBrand = this.baseStoreService.getBrand(cmpBrand);
          brand = isValidBrand ? cmpBrand : BrandsEnum.default;
        }
        card.brandCorner = brand;
        return this.toIVisualCard(card);
      });

      this.setLoaded();
      this.cdRef.markForCheck();
    });
  }

  toIVisualCard(cmp: VisualCardComponentInterface): IVisualCardWithConfig {
    return {
      card: {
        media: this.imageService.normalizeMedia(this.imageService.getMediaFromMediaContainerObject(cmp.mediaContainer)),
        content: {
          heading: this.headingParagraphMapper.toHeadingParagraph(cmp),
          footnote: cmp.footnote
        },
        imageRatio: this.imageRatio,
        brandCorner: cmp?.brandCorner
      },
      themesConfig: ThemesMapper.mapBrandTheme(cmp.theme),
      hasModal: cmp.hasModal
    };
  }
}
